import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemedStyles } from '../../theme/useThemedStyles';
import { ModalBackdrop } from '../common/ModalBackdrop';
import { formatDate as formatDisplayDate } from '../../utils/dateFormat';

interface TreatmentStep {
  id: number;
  stepNumber: number;
  name: string;
  description?: string;
  cost: number;
  completed: boolean;
  completedAt?: string;
  completedBy?: string;
  createdBy?: string;
  updatedAt?: string;
}

interface DetailedViewProps {
  open: boolean;
  onClose: () => void;
  treatmentId: number;
  treatmentName: string;
  totalCost: number;
  cashPaid: number;
  reservationCreditApplied: number;
  totalPaid: number;
  totalDue: number;
  paymentPercent: number;
  completedSteps: number;
  totalSteps: number;
  steps: TreatmentStep[];
  status: string;
  isCompleted: boolean;
  isMultiStep: boolean;
  onMarkStepComplete?: (treatmentId: number, stepId: number) => Promise<void>;
  onMarkStepIncomplete?: (treatmentId: number, stepId: number) => Promise<void>;
  onAddStep?: (treatmentId: number) => void;
  onDeleteStep?: (treatmentId: number, stepId: number) => void;
  onStatusChange?: (status: string) => void;
}

const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 16
};

const modalStyle: React.CSSProperties = {
  background: '#1e293b',
  borderRadius: 12,
  padding: 24,
  width: 'min(100%, 800px)',
  maxHeight: '90vh',
  overflow: 'auto',
  color: '#f1f5f9',
  border: '1px solid #334155'
};

const btn: React.CSSProperties = {
  padding: '6px 12px',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  background: '#667eea',
  color: '#fff'
};

const badge: React.CSSProperties = {
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  background: '#667eea',
  color: '#fff'
};

const progressBar = (percent: number, background: string): React.CSSProperties => ({
  height: 8,
  background,
  borderRadius: 4,
  overflow: 'hidden',
  marginTop: 8,
  position: 'relative'
});

const progressFill = (percent: number): React.CSSProperties => ({
  height: '100%',
  background: `hsl(${percent * 120}, 84%, 50%)`,
  width: `${percent}%`,
  transition: 'width 0.3s ease'
});

export const TreatmentDetailedView: React.FC<DetailedViewProps> = ({
  open,
  onClose,
  treatmentId,
  treatmentName,
  totalCost,
  cashPaid,
  reservationCreditApplied,
  totalPaid,
  totalDue,
  paymentPercent,
  completedSteps,
  totalSteps,
  steps,
  status,
  isCompleted,
  isMultiStep,
  onMarkStepComplete,
  onMarkStepIncomplete,
  onAddStep,
  onDeleteStep,
  onStatusChange
}) => {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [loading, setLoading] = useState<number | null>(null);

  if (!open) return null;

  const themedModalStyle: React.CSSProperties = {
    ...modalStyle,
    background: theme.surface,
    color: theme.text,
    border: `1px solid ${theme.border}`
  };
  const themedBtn: React.CSSProperties = { ...btn, ...theme.button };

  const handleMarkComplete = async (stepId: number) => {
    if (onMarkStepComplete) {
      setLoading(stepId);
      try {
        await onMarkStepComplete(treatmentId, stepId);
      } finally {
        setLoading(null);
      }
    }
  };

  const handleMarkIncomplete = async (stepId: number) => {
    if (onMarkStepIncomplete) {
      setLoading(stepId);
      try {
        await onMarkStepIncomplete(treatmentId, stepId);
      } finally {
        setLoading(null);
      }
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return '—';
    return formatDisplayDate(date);
  };

  return (
    <ModalBackdrop className="responsive-row" style={modalOverlay} onDismiss={onClose}>
      <div style={themedModalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
            <div>
              <h2 style={{ marginTop: 0, marginBottom: 8, fontSize: 24, fontWeight: 700 }}>
                {treatmentName}
              </h2>
              <div className="responsive-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ ...badge, background: '#0ea5e9' }}>
                  {status === 'in_progress' ? t('treatmentsUi.inProgress') : status === 'completed' ? t('treatmentsUi.completed') : status === 'archived' ? t('treatmentsUi.archived') : status === 'with_debt' ? t('treatmentsUi.withDebt') : status.replace('_', ' ')}
                </span>
                {isCompleted && (
                  <span style={{ ...badge, background: '#10b981' }}>✓ {t('treatmentsUi.complete')}</span>
                )}
                {isMultiStep && (
                  <span style={{ ...badge, background: '#8b5cf6' }}>{t('treatmentsUi.multiStepTreatment')}</span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: theme.muted,
                fontSize: 24,
                cursor: 'pointer',
                padding: 0
              }}
            >
              ×
            </button>
          </div>
          <div style={{ borderBottom: `1px solid ${theme.border}` }} />
        </div>

        {/* Financial Overview */}
        <div style={{ marginBottom: 24, padding: 16, background: theme.inputBg, borderRadius: 8 }}>
          <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>
                {t('treatmentsUi.totalCost')}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>
                Bs {totalCost.toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>
                {t('treatmentsUi.cashPaid', 'Cash paid')}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>
                Bs {cashPaid.toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>
                {t('treatmentsUi.reservationCredit', 'Reservation credit')}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0ea5e9' }}>
                Bs {reservationCreditApplied.toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>
                {t('treatmentsUi.due')}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: totalDue > 0 ? '#ef4444' : '#10b981' }}>
                Bs {totalDue.toFixed(2)}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: theme.muted, marginBottom: 6, fontWeight: 600 }}>
              {t('treatmentsUi.paymentProgress')}: {paymentPercent.toFixed(0)}%
            </div>
            <div style={progressBar(paymentPercent, theme.surfaceAlt)}>
              <div style={progressFill(paymentPercent)} />
            </div>
          </div>
        </div>

        {/* Treatment Steps */}
        {isMultiStep && (
          <div style={{ marginBottom: 24 }}>
            <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                {t('treatmentsUi.treatmentSteps')} ({completedSteps}/{totalSteps})
              </h3>
              {onAddStep && (
                <button
                  onClick={() => onAddStep(treatmentId)}
                  style={{ ...themedBtn, background: '#10b981', borderColor: '#10b981' }}
                >
                  + {t('treatmentsUi.addStep')}
                </button>
              )}
            </div>

            {/* Step Progress */}
            <div style={{ marginBottom: 16, padding: 12, background: theme.inputBg, borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: theme.muted, marginBottom: 6, fontWeight: 600 }}>
                {t('treatmentsUi.stepsCompleted')}: {completedSteps}/{totalSteps}
              </div>
              <div style={progressBar((completedSteps / totalSteps) * 100, theme.surfaceAlt)}>
                <div style={progressFill((completedSteps / totalSteps) * 100)} />
              </div>
            </div>

            {/* Steps List */}
            <div style={{ display: 'grid', gap: 12 }}>
              {steps.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: theme.muted, background: theme.inputBg, borderRadius: 8 }}>
                  {t('treatmentsUi.noSteps')}
                </div>
              ) : (
                steps.map((step) => (
                  <div
                    key={step.id}
                    style={{
                      padding: 16,
                      background: step.completed ? theme.inputBg : theme.surfaceAlt,
                      borderRadius: 8,
                      border: `1px solid ${step.completed ? '#10b981' : theme.border}`,
                      opacity: step.completed ? 0.8 : 1
                    }}
                  >
                    <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                      <div>
                        <div className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span className="responsive-row" style={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: step.completed ? '#10b981' : theme.border,
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 600
                          }}>
                            {step.completed ? '✓' : step.stepNumber}
                          </span>
                          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                            {step.name}
                          </h4>
                          <span style={{ ...badge, background: '#667eea' }}>
                            Bs {step.cost.toFixed(2)}
                          </span>
                        </div>
                        {step.description && (
                          <div style={{ fontSize: 12, color: theme.muted, marginLeft: 32, marginTop: 4 }}>
                            {step.description}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: theme.muted, textAlign: 'right' }}>
                        {step.completed && (
                          <div style={{ marginBottom: 6 }}>
                            ✓ {formatDate(step.completedAt)}
                          </div>
                        )}
                      </div>
                    </div>

                    {step.description && (
                      <div style={{ marginBottom: 12, paddingLeft: 32 }} />
                    )}

                    {/* Step Actions */}
                    <div className="responsive-row" style={{ display: 'flex', gap: 8, marginLeft: 32 }}>
                      {!step.completed && onMarkStepComplete && (
                        <button
                          onClick={() => handleMarkComplete(step.id)}
                          disabled={loading === step.id}
                          style={{
                            ...themedBtn,
                            background: '#10b981',
                            borderColor: '#10b981',
                            opacity: loading === step.id ? 0.6 : 1,
                            cursor: loading === step.id ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {loading === step.id ? t('treatmentsUi.marking') : t('treatmentsUi.markComplete')}
                        </button>
                      )}
                      {step.completed && onMarkStepIncomplete && (
                        <button
                          onClick={() => handleMarkIncomplete(step.id)}
                          disabled={loading === step.id}
                          style={{
                            ...themedBtn,
                            background: '#f59e0b',
                            borderColor: '#f59e0b',
                            opacity: loading === step.id ? 0.6 : 1,
                            cursor: loading === step.id ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {loading === step.id ? t('treatmentsUi.undoing') : t('treatmentsUi.markIncomplete')}
                        </button>
                      )}
                      {onDeleteStep && (
                        <button
                          onClick={() => onDeleteStep(treatmentId, step.id)}
                          style={{ ...themedBtn, background: '#ef4444', borderColor: '#ef4444' }}
                        >
                          {t('treatmentsUi.delete')}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Status Controls */}
        {onStatusChange && (
          <div style={{ marginBottom: 24, padding: 16, background: theme.inputBg, borderRadius: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: theme.muted, marginBottom: 8, display: 'block' }}>
              {t('treatmentsUi.treatmentStatus')}
            </label>
            <select
              onChange={(e) => onStatusChange(e.target.value)}
              defaultValue={status}
              style={{
                width: '100%',
                padding: 10,
                borderRadius: 6,
                ...theme.input,
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              <option value="in_progress">{t('treatmentsUi.inProgress')}</option>
              <option value="completed">{t('treatmentsUi.completed')}</option>
              <option value="archived">{t('treatmentsUi.archived')}</option>
              <option value="with_debt">{t('treatmentsUi.withDebt')}</option>
            </select>
          </div>
        )}

        {/* Footer */}
        <div className="responsive-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
          <button
            onClick={onClose}
            style={{ ...themedBtn, ...theme.secondaryButton }}
          >
            {t('treatmentsUi.close')}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
};

export default TreatmentDetailedView;
